import type { FormId } from '@risksmart-app/shared/forms/formConfigRegistry';
import { getFormConfigRegistry } from '@risksmart-app/shared/forms/formConfigRegistry';
import type { FormConfig } from '@risksmart-app/shared/forms/types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useModulesStore } from 'src/context/moduleContext';

import { useRiskSmartForm } from '@/components/form/form/customisable-form/RiskSmartFormContext';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { buildActiveFormDescriptor } from './buildActiveFormDescriptor';
import { createApplyPrefill } from './createApplyPrefill';
import type { ApplyPrefillField } from './types';
import { useActiveFormStore } from './useActiveFormStore';

/**
 * Publishes the active form's prefillable state to the `useActiveFormStore`
 * bridge so the AI chat panel (mounted outside this provider) can read the
 * form's fields and write proposed values into it. Rendered ONLY when a form
 * opts in via `publishActiveForm` (gate that at the call site on the feature
 * flag), and only for forms with a known `parentType`. Single-slot: it clears
 * the slot on unmount, mirroring `ToolbarStatePublisher`.
 *
 * `recordId` is supplied by the opt-in call site (which already resolves it via
 * `useGetGuidParam`), NOT derived from the route here: the generic publisher
 * can't reliably pick the form's own id off the route — param names vary
 * (`actionId`, `controlGroupId`, plain `id`) and a nested route like
 * `/risks/:riskId/controls/:controlId` exposes several ids at once.
 */
export const ActiveFormPublisher = <TFieldValues extends FieldValues>({
  methods,
  parentType,
  recordId,
}: {
  methods: UseFormReturn<TFieldValues>;
  parentType: FormId;
  recordId: string | null;
}) => {
  const { allFieldIds, defaultRequiredFields, forcedRequiredFields } =
    useRiskSmartForm();
  const modules = useModulesStore((s) => s.modules);
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');

  const setFormState = useActiveFormStore((s) => s.setFormState);
  const clearFormState = useActiveFormStore((s) => s.clearFormState);

  const descriptor = useMemo(() => {
    const formConfig = getFormConfigRegistry(formSectionsEnabled, modules)[
      parentType
    ] as FormConfig;
    const requiredFieldIds = new Set([
      ...defaultRequiredFields,
      ...forcedRequiredFields,
    ]);

    return buildActiveFormDescriptor(formConfig, allFieldIds, requiredFieldIds);
  }, [
    parentType,
    formSectionsEnabled,
    modules,
    allFieldIds,
    defaultRequiredFields,
    forcedRequiredFields,
  ]);

  // Read the latest methods / descriptor through refs so the published
  // `applyPrefill` / `getValues` stay referentially stable and the publish
  // effect below depends only on the DATA (descriptor / record / entity), not on
  // the per-render identity of `methods` — mirrors ToolbarStatePublisher (CUS-134).
  const methodsRef = useRef(methods);
  methodsRef.current = methods;
  const descriptorRef = useRef(descriptor);
  descriptorRef.current = descriptor;

  const getValues = useCallback(
    (): Record<string, unknown> => methodsRef.current.getValues(),
    []
  );

  const applyPrefill = useCallback((fields: ApplyPrefillField[]) => {
    const setFieldValue = (name: string, value: unknown): void => {
      // RHF's setValue is keyed by FieldPath<TFieldValues>; prefill targets are
      // dynamic canonical names already validated against the descriptor, so the
      // generic-string setter is the correct shape at this boundary.
      (
        methodsRef.current.setValue as (
          n: string,
          v: unknown,
          o: { shouldDirty: boolean; shouldValidate: boolean }
        ) => void
      )(name, value, { shouldDirty: true, shouldValidate: true });
    };

    return createApplyPrefill(descriptorRef.current, setFieldValue)(fields);
  }, []);

  // Publish the latest state on change. Deliberately NO cleanup here: an effect
  // cleanup runs before every re-run, so clearing here would blip the slot to
  // `null` on every descriptor/record change — a reactive consumer (the chat
  // store) could observe that null and discard its proposal. Clearing is an
  // unmount-only concern, handled by the separate effect below.
  useEffect(() => {
    setFormState({
      entityType: parentType,
      recordId,
      descriptor,
      applyPrefill,
      getValues,
    });
  }, [parentType, recordId, descriptor, applyPrefill, getValues, setFormState]);

  // Clear the slot only when the publisher unmounts (tab switch / navigation),
  // never on a mid-life update.
  useEffect(() => () => clearFormState(), [clearFormState]);

  return null;
};
